import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider, FormControlLabel,
    Grid,
    makeStyles, Radio, RadioGroup,
} from "@material-ui/core";
import { LcmDialogProps } from "./DialogProps";
import { Controller, useForm } from "react-hook-form";
import { nameof } from "ts-simple-nameof";
import { fieldLabels } from "../../sections/fieldlabels";
import { Error } from "../../common/Error";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { updateLcmStatus } from "../../../redux/actions";
import { useDispatch } from "react-redux";
import { CloseCaseModel } from "../../../domain/CloseCaseModel";
import { LcmStatus } from "../../../domain/LcmModel";

const defaultValues: Partial<CloseCaseModel> = {
    note: "",
    closeType: undefined
};

const useStyles = makeStyles(() => ({
    actions: {
        padding: 15,
    },
}));

export const CloseCaseDialog: React.FC<LcmDialogProps> = ({
    lcmItem,
    onClose,
}) => {
    const { handleSubmit, control, errors } = useForm<CloseCaseModel>({
        defaultValues,
    });

    const classes = useStyles();

    const dispatch = useDispatch();

    const onSubmit = (closeCaseModel: CloseCaseModel) => {
        if (closeCaseModel.closeType) {
            dispatch(updateLcmStatus(lcmItem.id, closeCaseModel.closeType, closeCaseModel.note));
            onClose();
        }
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            data-test="close-case-dialog"
        >
            <DialogTitle data-test="activity-title">Close case</DialogTitle>
            <Divider component="div" />
            <DialogContent style={{ minHeight: "250px" }}>
                <form>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name={nameof<CloseCaseModel>((x) => x.closeType)}
                                rules={{ required: true, validate: (value) => !(value == lcmItem.status) }}
                                render={({ onChange, value }) => (
                                    <>
                                        <label>{fieldLabels.caseCloseType}*</label>
                                        <RadioGroup
                                            aria-labelledby="close-type-radio-buttons-group-label"
                                            name="radio-buttons-group"
                                            
                                            value={value}
                                            onChange={onChange}
                                        >
                                            <FormControlLabel control={<Radio value={LcmStatus.Closed} checked={value == LcmStatus.Closed} data-test="closed-radio" />}
                                                label={fieldLabels.closeTypeClose} />
                                            <FormControlLabel control={<Radio value={LcmStatus.Cancelled} checked={value == LcmStatus.Cancelled} data-test="cancel-radio"/>}
                                                label={fieldLabels.closeTypeCancel} />
                                        </RadioGroup>
                                    </>
                                )}
                            />
                            {errors.closeType?.type === "required" && (
                                <Error data-test="case-close-type-validation">
                                    This field is required
                                </Error>
                            )}
                            {errors.closeType?.type === "validate" && (
                                <Error data-test="close-type-same-as-case-status-validation">
                                    This case has already been { LcmStatus[lcmItem.status].toLowerCase() }
                                </Error>
                            )}
                        </Grid>

                        <Grid item xs={12}>
                            <Controller
                                control={control}
                                name={nameof<CloseCaseModel>((x) => x.note)}
                                rules={{ required: true, maxLength: 5000 }}
                                render={({ onChange, value }) => (
                                    <>
                                        <label>{fieldLabels.caseNote}*</label>
                                        <MarkdownEditor
                                            onChange={onChange}
                                            value={value}
                                            dataTest="close-case-note-text-field"
                                        />
                                    </>
                                )}
                            />
                            {errors.note?.type === "required" && (
                                <Error data-test="close-case-note-text-validation">
                                    This field is required
                                </Error>
                            )}
                            {errors.note?.type === "maxLength" && (
                                <Error data-test="summary-length-validation">
                                    Max length exceeded
                                </Error>
                            )}
                        </Grid>
                    </Grid>
                </form>
            </DialogContent>
            <DialogActions className={classes.actions}>
                <Button
                    onClick={onClose}
                    color="primary"
                    data-test="return-close-case-button"
                >
                    Return
                </Button>
                <Button
                    onClick={handleSubmit(onSubmit)}
                    color="primary"
                    data-test="continue-close-case-button"
                >
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};
